import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
import { PlayerHeader } from '../shared';
import VitalStrip from '../shared/VitalStrip';
import TabNavigator from './TabNavigator';
import QuickActionsBar from './QuickActionsBar';
import usePlayerData, { invalidatePlayer } from '../hooks/usePlayerData';
import MobilePositionAssignmentModal from '../../MobilePositionAssignmentModal';
import MobileRosterPlanModal from '../../MobileRosterPlanModal';
import MobileDropDesignationModal from '../../MobileDropDesignationModal';
import { useRosterPlan } from '../../../../../hooks/rosterPlan';
import { leaguesAPI } from '../../../../../services/apiService';
import { formatApiError } from '../../../../../services/errorUtils';
import { BOTTOM_NAV_HEIGHT } from '../../../../../services/mobileNavTheme';
import { analyzeRosterCapacity, canAddPlayers } from '../../../../../utils/RosterCapacityUtils';
import { useAssignmentLeagueConfig } from '../../../../../hooks/useAssignmentLeagueConfig';
import { getWaiverActionState } from '../../../../../utils/waiverStatus';
import { getOwnershipState, OWNERSHIP } from '../../../../../utils/playerOwnership';
import { useWaiverClaimFlow } from '../../../../../hooks/useWaiverClaimFlow';
import MobilePlayerCard from '../card/MobilePlayerCard';
// Lazy load tab components for better performance
const StatsTabContent = lazy(() => import('./StatsTabContent'));
const GameLogsTabContent = lazy(() => import('./GameLogsTabContent'));
const ScoutingTabContent = lazy(() => import('./ScoutingTabContent'));
const InfoTabContent = lazy(() => import('./InfoTabContent'));
const NewsTabContent = lazy(() => import('./NewsTabContent'));
const FinancialsTabContent = lazy(() => import('./FinancialsTabContent'));

/**
 * MobilePlayerModal Component
 * 
 * Slide-up modal that displays player stats and quick actions.
 * Covers 70-80% of screen, dismissible by swipe-down or backdrop tap.
 * 
 * @component
 * @example
 * <MobilePlayerModal
 *   playerId="545361"
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 *   leagueId="league_123"
 * />
 */
const MobilePlayerModal = ({ playerId, isOpen, onClose, onExpandToCard, leagueId, onSwap }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('Info');
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(0);
  const [touchCurrent, setTouchCurrent] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Refs
  const modalRef = useRef(null);
  const dragHandleRef = useRef(null);
  
  // Fetch player data
  const { data, loading, error, refetch } = usePlayerData(
    playerId,
    leagueId,
    isOpen && !!playerId
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setActiveTab('Info');
      setTranslateY(0);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Touch event handlers for swipe-down gesture
  const handleTouchStart = (e) => {
    // Only allow drag from drag handle or header area
    const target = e.target;
    const isDragHandle = dragHandleRef.current?.contains(target);
    const isHeader = target.closest('[data-header="true"]');
    
    if (!isDragHandle && !isHeader) return;
    
    setTouchStart(e.touches[0].clientY);
    setTouchCurrent(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    setTouchCurrent(currentY);
    
    const diff = currentY - touchStart;
    
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setTranslateY(diff);
      // Add some resistance to the drag
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const dragDistance = touchCurrent - touchStart;
    
    // If dragged down more than 100px, close modal
    if (dragDistance > 100) {
      handleClose();
    } else {
      // Snap back to original position
      setTranslateY(0);
    }
    
    setTouchStart(0);
    setTouchCurrent(0);
  };

  // Close handler
  const handleClose = () => {
    setTranslateY(0);
    setIsDragging(false);
    onClose();
  };

  // Backdrop click handler
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Position assignment modal state
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [currentRoster, setCurrentRoster] = useState([]);
  const [loadingRosterData, setLoadingRosterData] = useState(false);

  // DD-FIXES-0705 Fix 2 (Bug 2 root cause): the league config for the
  // assignment modal comes from the ONE canonical source — the shared hook
  // returning the §5-shaped league object (roster_settings NESTED). This
  // file previously hand-built a FLAT league object here, which the modal's
  // canonical-shape guard rejected → false "All Active Positions Full"
  // (BUG_2_MODAL_ADD_INVESTIGATION_20260705.md). NOTHING IS FLATTENED.
  const {
    league: assignmentLeague,
    error: leagueConfigError,
  } = useAssignmentLeagueConfig(leagueId);

  // Chunk 3.16 Bug 1: proactive roster-capacity gate + drop designation for
  // the mobile player-profile ADD. When the roster is full on TOTAL capacity,
  // surface the drop/rearrange prompt (MobileDropDesignationModal) BEFORE
  // position assignment — parity with the desktop player-modal add. (The
  // §4.5.20 onOpenDropShuffle CTA only covers the per-position-full case.)
  const [showDropModal, setShowDropModal] = useState(false);
  const [designatedDropId, setDesignatedDropId] = useState(null);

  // Bug 1 follow-up (Bible § 4.5.20, 2026-05-15): drop-shuffle plan for
  // mobile player-profile-modal ADD button. Mirrors MobileFreeAgentSearch
  // wiring. The MobilePlayerModal didn't pass onOpenDropShuffle, so the
  // new empty-active panel inside MobilePositionAssignmentModal showed
  // the warning without the prominent Drop CTA.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: data,
    currentRoster,
    isCommissionerMode: false,
    getTargetTeamId: null,
  });

  // DD-WAIVER-0723-BUILD W2: waiver-claim flow for the mobile player-profile
  // ADD. An on-waivers player must never enter the fa_add capacity gate or
  // position assignment — the claim branch in handleAdd fires first. The
  // hook resolves the viewer's team id for cooling-off checks (this modal
  // has no userTeam prop).
  const waiverClaim = useWaiverClaimFlow({
    leagueId,
    league: assignmentLeague,
    fetchMyTeamId: isOpen && !!leagueId,
    onSuccess: (claimedPlayer, message) => {
      // DD-MODAL-0723: the claim changed this player's pending state.
      invalidatePlayer(leagueId, claimedPlayer?.ids?.mlb);
      alert(message);
      onClose();
    },
    onError: (message) => { alert(message); },
  });

  // Load the current roster when Add is clicked. Returns the freshly loaded
  // capacity-relevant players so callers can analyze capacity in the same
  // tick (React state set here is not yet readable synchronously). League
  // config is NOT loaded here — it comes from useAssignmentLeagueConfig
  // above (one canonical source, one shape — DD-FIXES-0705 Fix 2).
  const loadRosterData = async () => {
    if (!leagueId || !playerId) return null;

    setLoadingRosterData(true);
    try {
      let players = [];
      const rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId);
      if (rosterResponse.success && rosterResponse.players) {
        setCurrentRoster(rosterResponse.players);
        // Exclude former/inactive for capacity counting (matches usePlayerAssignment).
        players = rosterResponse.players.filter(p => {
          const status = p.league_context?.roster?.status;
          return status && status !== 'former' && status !== 'inactive';
        });
      }
      return { players };
    } catch (err) {
      console.error('Error loading roster data:', err);
      return null;
    } finally {
      setLoadingRosterData(false);
    }
  };

  // Quick action handlers
  const handleAdd = async (pId) => {
    if (!leagueId) {
      console.warn('No league ID provided for adding player');
      return;
    }

    // DD-MODAL-0723 (Rule 9): never launch an ADD on a player whose
    // ownership is unknown or who is already owned — the backend refuses
    // both, but the UI must not offer confident wrong affordances.
    const ownershipGate = getOwnershipState(data);
    if (ownershipGate.state !== OWNERSHIP.FREE_AGENT) {
      alert(ownershipGate.state === OWNERSHIP.OWNED
        ? 'This player is already on a roster.'
        : 'Ownership data unavailable — reopen the player and try again.');
      return;
    }

    // W2 waiver gate (Bible § 4.5.19): an on-waivers player with an active
    // hold gets the CLAIM flow, never position assignment / plain ADD.
    // Post-expiry falls through to the existing ADD behavior.
    const waiverState = getWaiverActionState(data, waiverClaim.myTeamId);
    if (waiverState.onWaivers && !waiverState.expired) {
      if (waiverState.coolingOff) return; // Add cell is disabled — defensive only
      const loaded = await loadRosterData();
      waiverClaim.openClaim(data, { currentRoster: loaded?.players || [] });
      return;
    }

    // Fail loud (Rule 9): a league-config failure is a config failure, not a
    // roster condition. Never proceed into slot math without the canonical
    // nested league object.
    if (leagueConfigError || !assignmentLeague) {
      alert(`Cannot add player — league configuration unavailable: ${leagueConfigError || 'still loading, try again'}`);
      return;
    }

    const loaded = await loadRosterData();
    if (!loaded) {
      alert('Cannot add player — roster could not be loaded. Please try again.');
      return;
    }

    // Bug 1: proactive total-capacity gate (parity with desktop player modal).
    // When the roster is full, surface the drop/rearrange prompt before
    // position assignment. Capacity analyzes against the SAME canonical
    // league object the assignment modal receives (Rule 12).
    const analysis = analyzeRosterCapacity(assignmentLeague, loaded.players);
    if (analysis && !canAddPlayers(1, analysis).canAdd) {
      setDesignatedDropId(null);
      setShowDropModal(true);
      return;
    }

    setShowPositionModal(true);
  };

  // Handle position assignment submission
  const handlePositionAssignment = async ({ player, assignment, roster_status, roster_position, mode }) => {
    try {
      if (!leagueId || !data) return;

      // Get player salary from financial data
      const playerSalary = data.league_context?.financial?.baseline_price ?? data.league_context?.financial?.contract_salary;
      
      // Minors players have 0 salary
      const finalSalary = roster_status === 'minors' ? 0 : playerSalary;
      const contractYears = roster_status === 'minors' ? 0 : 2;

      const playerData = {
        league_player_id: data.ids?.league_player || playerId,
        salary: finalSalary,
        contract_years: contractYears,
        roster_status: roster_status || 'active',
        roster_position: roster_position || null,
        start_contract: roster_status !== 'minors',
        // Bug 1: when the roster was full, the user designated a drop via
        // MobileDropDesignationModal — release it as part of this add.
        drop_player_id: designatedDropId || null
      };

      const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);

      if (response.success) {
        // Close modals and refresh
        setShowPositionModal(false);
        setDesignatedDropId(null);
        onClose();
        // Optionally refresh player data
        if (refetch) refetch();
        
        // Show success message (could be improved with a toast)
        alert(`${data.info?.full_name || 'Player'} added to roster!`);
        
        // Dispatch event to refresh roster displays
        window.dispatchEvent(new Event('roster-updated'));
      } else {
        alert(response.message || response.error || 'Failed to add player');
      }
    } catch (err) {
      console.error('Error adding player:', err);
      // DD-NIMMO-422-FE: surface the server's message (DL-residency lock, etc.)
      alert(formatApiError(err, 'Failed to add player. Please try again.'));
    }
  };

  const handleCancelPositionModal = () => {
    setShowPositionModal(false);
  };

  const handleDrop = async (pId) => {
    const playerName = data?.info?.full_name || 'this player';
    const leaguePlayerId = data?.ids?.league_player;
    if (!leaguePlayerId) {
      alert('Error: Player ID not found');
      return;
    }

    try {
      const response = await leaguesAPI.dropPlayerFromTeam(leagueId, leaguePlayerId);
      if (response.success) {
        onClose();
        window.dispatchEvent(new Event('roster-updated'));
      } else {
        alert(response.message || 'Failed to drop player');
      }
    } catch (err) {
      console.error('Error dropping player:', err);
      // DD-NIMMO-422-FE: surface the server's message (DL-residency lock, etc.)
      alert(formatApiError(err, 'Failed to drop player'));
    }
  };

  const handleTrade = (pId) => {
    console.log('Trade player:', pId);
    // TODO: Implement trade initiation
  };

  // 9l.2: removed handleWatch stub — Watch action rendered by mobile-internal
  // <WatchButton /> inside QuickActionsBar. Self-contained API + state.

  // DD-FIXES-0705 Fix 3 (Option 1, owner-ratified): the modal OWNS a default
  // expand-to-full path. Pre-fix, handleViewFull delegated to the
  // onExpandToCard prop and silently no-op'd on the three pages that never
  // passed it (MobileProbables / MobileTeamBoxScore / MobileBoxScoreTile —
  // BUG_3_FULL_BUTTON_INVESTIGATION_20260705.md). Now:
  //   - pages that pass onExpandToCard keep their custom sequencing
  //     (override takes precedence, internal card never mounts), and
  //   - every other page gets the internal <MobilePlayerCard> below.
  // FULL can no longer be a dead control on any page, by construction.
  const [internalCardOpen, setInternalCardOpen] = useState(false);

  const handleViewFull = () => {
    if (onExpandToCard) {
      onExpandToCard();
      return;
    }
    setInternalCardOpen(true);
  };

  // Reset the internal card whenever the modal is (re)opened or closed so a
  // stale expanded state can't flash on the next open.
  useEffect(() => {
    if (!isOpen) setInternalCardOpen(false);
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) return null;

  // Internal expand-to-full (default path — no onExpandToCard override).
  // The full-screen card replaces the sheet; Return ↓ collapses back here,
  // Close dismisses everything via the parent's onClose.
  if (internalCardOpen && !onExpandToCard) {
    return (
      <MobilePlayerCard
        playerId={playerId}
        leagueId={leagueId}
        isOpen={internalCardOpen}
        onClose={() => { setInternalCardOpen(false); handleClose(); }}
        onCollapse={() => setInternalCardOpen(false)}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={styles.backdrop} onClick={handleBackdropClick}>
        <div className="mpc-modal-sheet" style={styles.modal}>
          <div style={styles.dragHandle} />
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner} />
            <div style={styles.loadingText}>Loading player data...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div style={styles.backdrop} onClick={handleBackdropClick}>
        <div className="mpc-modal-sheet" style={styles.modal}>
          <div style={styles.dragHandle} />
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠️</div>
            <div style={styles.errorText}>
              {error || 'Failed to load player data'}
            </div>
            <button style={styles.retryButton} onClick={refetch}>
              Retry
            </button>
            <button style={styles.closeButton} onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate modal style with transform for swipe gesture
  const modalStyle = {
    ...styles.modal,
    transform: `translateY(${translateY}px)`,
    transition: isDragging ? 'none' : 'transform 300ms ease-out'
  };

  // Get content based on active tab (wrapped in Suspense)
  const getTabContent = () => {
    switch (activeTab) {
      case 'Stats':
        return <StatsTabContent player={data} playerId={playerId} leagueId={leagueId} statsSeasonYear={data?.stats_season_year} />;
      case 'Logs':
        return <GameLogsTabContent player={data} playerId={playerId} leagueId={leagueId} fill />;
      case 'Scouting':
        return <ScoutingTabContent player={data} />;
      case 'Info':
        return <InfoTabContent player={data} />;
      case 'News':
        return <NewsTabContent playerId={data?.ids?.mlb || playerId} />;
      case 'FIN':
        return <FinancialsTabContent player={data} />;
      default:
        return null;
    }
  };

  return (
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className="mpc-modal-sheet"
        style={modalStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div ref={dragHandleRef} style={styles.dragHandleContainer}>
          <div style={styles.dragHandle} />
        </div>

        {/* Close Button */}
        <button style={styles.closeButtonTop} onClick={handleClose}>
          ✕
        </button>

        {/* Player Header */}
        <div data-header="true" style={{ flexShrink: 0 }}>
          <PlayerHeader
            player={data}
            size="standard"
          />
        </div>

        {/* LOCKED MODAL BOUNDARY: all modal content and rules inset 4px per side.
            One structural inset at the body so every child — VitalStrip, the tab
            bar, the tab content, and any future tab — and every horizontal
            hairline within them ends 4px from each screen edge. Do not add
            per-component side padding; do not let any divider run full-bleed. */}
        <div style={styles.bodyInset}>
          {/* 5-cell VitalStrip: Grade · %ile · Price · Mkt Val · MLB rank.
              flexShrink:0 — fixed chrome; only .modal-content-scroll flexes. */}
          <div style={{ flexShrink: 0 }}>
            <VitalStrip player={data} />
          </div>

          {/* Tab Navigator (MTabs) — 6 open-row tabs (Info first; opens on Stats) */}
          <div style={{ flexShrink: 0 }}>
            <TabNavigator
              tabs={['Info', 'Stats', 'Logs', 'Scouting', 'News', 'FIN']}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {/* Tab Content (scrollable, hidden scrollbar) */}
          <div className="modal-content-scroll" style={styles.contentContainer}>
            <Suspense fallback={<TabLoadingSkeleton />}>
              {getTabContent()}
            </Suspense>
          </div>
        </div>

        {/* Quick Actions Bar. W2 (Bible § 4.5.19): CLAIM affordance for
            on-waivers players; Cooling Off (disabled) for the team that
            cut them, until the hold expires. */}
        {(() => {
          const waiverAction = getWaiverActionState(data, waiverClaim.myTeamId);
          const isActiveWaiver = waiverAction.onWaivers && !waiverAction.expired;
          // Canonical ownership predicate (Bible §4 / DD-MODAL-0723,
          // Rule 12 — utils/playerOwnership). UNKNOWN disables the
          // add/drop affordances entirely (Rule 9: no confident action
          // on absent ownership data).
          const ownership = getOwnershipState(data);
          const ownershipUnknown = ownership.state === OWNERSHIP.UNKNOWN;
          return (
            <QuickActionsBar
              playerId={playerId}
              onAdd={handleAdd}
              onDrop={handleDrop}
              onTrade={handleTrade}
              // DD-RO-MOBILE-SWAP-0727: emit the player up to the parent (FA search / roster), which owns the
              // 1-1 swap flow for its direction and opens MobileFASwapSheet — the shared modal stays decoupled
              // from the optimizer. Only offered when the caller wired a handler and ownership is known.
              onSwap={onSwap && !ownershipUnknown ? () => { onClose(); onSwap(data); } : undefined}
              onViewFull={handleViewFull}
              isOnRoster={ownership.state === OWNERSHIP.OWNED}
              showActions={!!leagueId}
              playerName={data.info?.full_name || (data.info?.first_name && data.info?.last_name ? `${data.info.first_name} ${data.info.last_name}` : null)}
              addLabel={ownershipUnknown ? 'Unavailable' : (isActiveWaiver ? (waiverAction.coolingOff ? 'Cooling Off' : 'Claim') : 'Add')}
              addDisabled={ownershipUnknown || waiverAction.coolingOff}
            />
          );
        })()}
      </div>

      {/* Position Assignment Modal */}
      {showPositionModal && data && assignmentLeague && (
        <MobilePositionAssignmentModal
          player={data}
          league={assignmentLeague}
          currentRoster={currentRoster}
          onAssign={handlePositionAssignment}
          onCancel={handleCancelPositionModal}
          isVisible={showPositionModal}
          mode="add"
          currentStatus={null}
          currentPosition={null}
          onOpenDropShuffle={() => {
            // Close position-assignment modal but preserve player + roster
            // state for the RosterPlanModal that pops next.
            setShowPositionModal(false);
            rosterPlan.open();
          }}
        />
      )}

      {/* MobileRosterPlanModal — drop-shuffle plan flow for the player-
          profile-modal ADD button. Per Bible § 4.5.10 atomic drop+add. */}
      {data && assignmentLeague && (
        <MobileRosterPlanModal
          candidate={data}
          currentRoster={currentRoster}
          state={rosterPlan.state}
          plans={rosterPlan.plans}
          selectedDropPlayer={rosterPlan.selectedDropPlayer}
          selectedPlan={rosterPlan.selectedPlan}
          errorClass={rosterPlan.errorClass}
          errorMessage={rosterPlan.errorMessage}
          infeasibleReason={rosterPlan.infeasibleReason}
          onPickDrop={rosterPlan.requestPlans}
          onChangeDrop={rosterPlan.changeDrop}
          onSelectPlan={rosterPlan.selectPlan}
          onConfirm={rosterPlan.executePlan}
          onCancel={() => rosterPlan.cancel()}
          isVisible={rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'}
        />
      )}

      {/* Bug 1: proactive drop-designation when the roster is full — parity
          with the desktop player-modal add (FreeAgentSearch DropDesignation
          flow). Designate a release, then continue to position assignment
          with drop_player_id. */}
      <MobileDropDesignationModal
        isOpen={showDropModal}
        onClose={() => {
          setShowDropModal(false);
          setDesignatedDropId(null);
        }}
        onConfirm={(dropPlayerId) => {
          setDesignatedDropId(dropPlayerId);
          setShowDropModal(false);
          setShowPositionModal(true);
        }}
        playerBeingAdded={data}
        currentRoster={currentRoster}
        leagueId={leagueId}
        context="fa_add"
        isRosterFull={true}
        // W2: real value from the player's canonical availability namespace
        // (was hardcoded null). Waiver priority is a league-level fact, not
        // on the player object — FA-search parity passes null.
        waiverPriority={null}
        totalTeams={null}
        waiverExpiresAt={data?.league_context?.availability?.waiver_expires_at || null}
      />

      {/* W2: waiver-claim modal (context='waiver_claim') — same submit flow
          as MobileFreeAgentSearch (leaguesAPI.submitWaiverClaim via
          useWaiverClaimFlow). */}
      <MobileDropDesignationModal {...waiverClaim.modalProps} />
    </div>
  );
};

MobilePlayerModal.propTypes = {
  /** Player MLB ID */
  playerId: PropTypes.string.isRequired,
  
  /** Whether modal is open */
  isOpen: PropTypes.bool.isRequired,
  
  /** Callback when modal closes */
  onClose: PropTypes.func.isRequired,
  
  /** Callback to expand to full card */
  onExpandToCard: PropTypes.func,
  
  /** League ID for context */
  leagueId: PropTypes.string
};

MobilePlayerModal.defaultProps = {
  onExpandToCard: null,
  leagueId: null
};

// Tab Loading Skeleton Component
const TabLoadingSkeleton = () => (
  <div style={styles.skeletonContainer}>
    <div style={styles.skeletonCard}>
      <div style={styles.skeletonLine} />
      <div style={styles.skeletonLine} />
      <div style={styles.skeletonLineShort} />
    </div>
    <div style={styles.skeletonCard}>
      <div style={styles.skeletonLine} />
      <div style={styles.skeletonLine} />
    </div>
  </div>
);

const C = dynastyTheme.tokens.colors;

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: withAlpha(C.black, 0.7),
    zIndex: 1050,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    animation: 'fadeIn 200ms ease-out'
  },
  
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: '430px',
    // Height comes from the .mpc-modal-sheet CSS class (65dvh with a 65vh
    // fallback) — inline styles can't hold the duplicate-property fallback. dvh =
    // the actual VISIBLE viewport (accounts for mobile browser chrome).
    maxHeight: '600px',
    // Sit ABOVE the fixed BottomNav (was a too-small 60px) so QuickActionsBar +
    // the table's sticky totals are above the nav, not behind it.
    marginBottom: BOTTOM_NAV_HEIGHT,
    background: C.ground.screen, // flat house dark — NO gradient (the gradient banded
    // through every tab's transparent areas; one uniform solid top to bottom).
    borderRadius: '16px 16px 0 0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'slideUp 300ms ease-out',
    boxShadow: `0 -4px 20px ${withAlpha(C.black, 0.3)}`
  },

  dragHandleContainer: {
    flexShrink: 0, // fixed top chrome — only .modal-content-scroll flexes
    padding: '8px 0 4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'grab',
    WebkitTapHighlightColor: 'transparent'
  },
  
  dragHandle: {
    width: '36px',
    height: '3px',
    backgroundColor: C.coolGray[720],
    borderRadius: '2px',
    opacity: 0.5
  },
  
  closeButtonTop: {
    position: 'absolute',
    top: '8px',
    right: '12px',
    width: '28px',
    height: '28px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: C.surface[850],
    color: C.textHi,
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    transition: 'all 150ms ease',
    WebkitTapHighlightColor: 'transparent'
  },
  
  // Locked modal boundary — single 4px-per-side inset for the whole body region
  // (VitalStrip + tab bar + tab content). Every child and every horizontal rule
  // within it ends 4px from each edge.
  bodyInset: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '4px',
    paddingRight: '4px',
    // 8px gap so the content scroller ends ABOVE QuickActionsBar (no overlay).
    marginBottom: '8px'
  },

  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    minHeight: 0,
    // Flex column (like the card's .mpc-content) so the fill-mode Game Logs table
    // resolves its maxHeight:'100%' against this bounded scroller and clips inside
    // it. Other tabs are natural-height flex items — unaffected.
    display: 'flex',
    flexDirection: 'column',
    // Locked: no visible scrollbars anywhere in the modal.
    scrollbarWidth: 'none',
    msOverflowStyle: 'none'
  },
  
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '20px'
  },
  
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: `4px solid ${C.surface[850]}`,
    borderTop: `4px solid ${C.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  
  loadingText: {
    fontSize: '16px',
    color: C.coolGray[550],
    fontWeight: '500'
  },

  errorContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '16px'
  },
  
  errorIcon: {
    fontSize: '48px',
    opacity: 0.7
  },
  
  errorText: {
    fontSize: '16px',
    color: C.textHi,
    textAlign: 'center',
    fontWeight: '500'
  },

  retryButton: {
    padding: '12px 24px',
    backgroundColor: C.primary,
    border: 'none',
    borderRadius: '8px',
    color: C.surface[798],
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'all 150ms ease',
    WebkitTapHighlightColor: 'transparent'
  },
  
  closeButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: `1px solid ${C.coolGray[780]}`,
    borderRadius: '8px',
    color: C.textHi,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    WebkitTapHighlightColor: 'transparent'
  },
  
  // Skeleton Styles
  skeletonContainer: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  
  skeletonCard: {
    background: C.surface[850],
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  
  skeletonLine: {
    height: '16px',
    background: `linear-gradient(90deg, ${C.coolGray[850]} 25%, ${C.tier.midBg} 50%, ${C.coolGray[850]} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '4px'
  },
  
  skeletonLineShort: {
    height: '16px',
    width: '60%',
    background: `linear-gradient(90deg, ${C.coolGray[850]} 25%, ${C.tier.midBg} 50%, ${C.coolGray[850]} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '4px'
  }
};

// Add shimmer animation
if (typeof window !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .modal-content-scroll::-webkit-scrollbar { display: none; }
    /* Sheet height to the VISIBLE viewport: 65vh fallback, 65dvh wins where
       supported (accounts for mobile browser chrome). */
    .mpc-modal-sheet { height: 65vh; height: 65dvh; }
  `;
  document.head.appendChild(styleSheet);
}

export default MobilePlayerModal;
